import React, { useState } from "react";
import CategoryManager from "../components/CategoryManager";
import MonthlyAmountCard from "../components/MonthlyAmountCard";
import SegmentedControl from "../components/SegmentedControl";
import { upsertAccount } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { useTheme, themes } from "../contexts/ThemeContext";
import { EXPENSE_KIND, INCOME_KIND } from "../constants/entryKinds";
import { FaSignOutAlt, FaCheck, FaPalette } from "react-icons/fa";

const SECTIONS = [
  { value: "general", label: "General" },
  { value: "income", label: "Income" },
  { value: "expenses", label: "Expenses" },
];

/**
 * @param {{ account: ReturnType<typeof import('../hooks/useAccount').useAccount> }} props
 */
const Settings = ({ account }) => {
  const [section, setSection] = useState("general");
  const [saveError, setSaveError] = useState("");
  const { signOut, user } = useAuth();
  const { themeName, setThemeName } = useTheme();

  const {
    categories,
    setCategories,
    budget,
    setBudget,
    incomeCategories,
    setIncomeCategories,
    incomeGoal,
    setIncomeGoal,
  } = account;

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      console.error("Error signing out:", error);
    }
  };

  /**
   * Optimistic write with rollback: commits to local state, persists, and
   * restores the previous value if the write fails — so the UI can't end up
   * showing a category that doesn't exist on the server.
   *
   * Note this runs *outside* the setter: a state updater must be pure, and
   * React 18's StrictMode invokes it twice in development, which would fire two
   * writes per edit.
   *
   * @param {'categories'|'incomeCategories'|'budget'|'incomeGoal'} field
   */
  const commit = async (field, next, setLocal, previous, message) => {
    setSaveError("");
    setLocal(next);
    try {
      // Only the changed field is sent. upsertAccount ignores omitted keys, so
      // saving a budget can't blank the income goal.
      await upsertAccount({ [field]: next });
    } catch (error) {
      console.error("Error updating account:", error);
      setLocal(previous);
      setSaveError(message);
    }
  };

  const commitCategories = (field, next, setLocal, previous) =>
    commit(field, next, setLocal, previous, "Couldn't save that change. Please try again.");

  const commitAmount = (field, next, setLocal, previous) =>
    commit(field, next, setLocal, previous, "Couldn't save that amount. Please try again.");

  return (
    <div className="bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen p-4">
      <h3 className="text-2xl font-bold text-gray-800 mb-4">Settings</h3>

      <SegmentedControl
        value={section}
        onChange={setSection}
        options={SECTIONS}
        ariaLabel="Settings section"
        className="mb-4"
      />

      {saveError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
          {saveError}
        </div>
      )}

      {section === "general" && (
        <>
          {/* Theme Section */}
          <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-4 mb-4">
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
              <FaPalette className="text-gray-400" />
              Theme
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(themes).map(([key, theme]) => (
                <button
                  key={key}
                  onClick={() => setThemeName(key)}
                  className={`relative p-3 rounded-xl border-2 transition-all ${
                    themeName === key
                      ? "border-gray-800 bg-gray-50"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                >
                  {/* Color preview dots, taken straight from the theme's own
                      palette so adding a theme can't leave this out of sync. */}
                  <div className="flex gap-1.5 mb-2">
                    <div
                      className="w-5 h-5 rounded-full"
                      style={{ backgroundColor: theme.colors.primary[500] }}
                    ></div>
                    <div
                      className="w-5 h-5 rounded-full"
                      style={{ backgroundColor: theme.colors.accent[500] }}
                    ></div>
                  </div>
                  <p className="text-sm font-medium text-gray-800 text-left">{theme.name}</p>
                  <p className="text-xs text-gray-500 text-left">{theme.description}</p>
                  {themeName === key && (
                    <div className="absolute top-2 right-2 w-5 h-5 bg-gray-800 rounded-full flex items-center justify-center">
                      <FaCheck className="text-white text-xs" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Account Section */}
          <div className="bg-white rounded-2xl shadow-soft border border-gray-100 p-4">
            <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Account</h4>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400">Signed in as</p>
                <p className="text-sm font-medium text-gray-700">{user?.email}</p>
              </div>
              <button
                onClick={handleSignOut}
                className="p-3 px-5 rounded-xl text-red-600 font-medium bg-red-50 hover:bg-red-100 border border-red-200 transition-all active:scale-95 flex items-center gap-2"
              >
                <FaSignOutAlt size={14} />
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}

      {section === "income" && (
        <>
          <CategoryManager
            title={INCOME_KIND.categoryPlural}
            categories={incomeCategories}
            placeholder={INCOME_KIND.newCategoryPlaceholder}
            onError={setSaveError}
            onChange={(next) =>
              commitCategories("incomeCategories", next, setIncomeCategories, incomeCategories)
            }
          />
          <MonthlyAmountCard
            title="Monthly Income Goal"
            value={incomeGoal}
            hint="Tracks money actually received. Outstanding payments don't count toward it."
            onSave={(next) =>
              commitAmount("incomeGoal", next, setIncomeGoal, incomeGoal)
            }
          />
        </>
      )}

      {section === "expenses" && (
        <>
          <CategoryManager
            title={EXPENSE_KIND.categoryPlural}
            categories={categories}
            placeholder={EXPENSE_KIND.newCategoryPlaceholder}
            onError={setSaveError}
            onChange={(next) =>
              commitCategories("categories", next, setCategories, categories)
            }
          />
          <MonthlyAmountCard
            title="Monthly Budget"
            value={budget}
            onSave={(next) => commitAmount("budget", next, setBudget, budget)}
          />
        </>
      )}
    </div>
  );
};

export default Settings;
